/**
 * LeaveRoomButton renders a direct "leave room" action in the header bar.
 *
 * Why: leaving used to only be reachable through the room menu; with video
 * calls now running, mates need a faster, more visible way to leave.
 * Duplicates RoomMenu's "Leave Room" action (same handler, no confirmation)
 * in the header's icon style.
 *
 * When to use: Rendered by Header in the right-side button group.
 */
import * as React from 'react'
import { cn } from '@merels/ui'
import { IconButton } from '../../library/IconButton/IconButton'
import { LogoutIcon } from '../../library/Icons/LogoutIcon'
import { headerTestIds } from './header.testIds'

export interface LeaveRoomButtonProps {
  /** Leaves the room. Matches RoomMenu's onLeaveRoom - no confirmation dialog. */
  onLeave: () => void
  /** Ref for focus management (callback or object ref) */
  buttonRef?: React.Ref<HTMLButtonElement>
  /** Tab index for focus navigation */
  tabIndex?: number
  /** Whether this button is currently focused */
  isFocused?: boolean
  /** Accent color for styling */
  accentColor?: string
  /** Ink when filled with accentColor (hover) */
  accentInkColor?: string
  /** Background color for focus ring */
  backgroundColor?: string
}

export function LeaveRoomButton({
  onLeave,
  buttonRef,
  tabIndex,
  isFocused,
  accentColor,
  accentInkColor = '#ffffff',
  backgroundColor,
}: LeaveRoomButtonProps) {
  return (
    <IconButton
      ref={buttonRef}
      onClick={onLeave}
      data-testid={headerTestIds.leaveRoomButton}
      className={cn(
        "flex h-[var(--menu-btn-size)] w-[var(--menu-btn-size)] items-center justify-center rounded-lg transition-colors",
        isFocused && "outline-none"
      )}
      style={{
        ...(accentColor ? { color: accentColor } : {}),
        ...(isFocused && accentColor ? {
          boxShadow: `0 0 0 2px ${accentColor}, 0 0 0 4px ${backgroundColor}`,
        } : {}),
      }}
      tabIndex={tabIndex}
      title="Leave room"
      aria-label="Leave room"
      onMouseEnter={(e) => {
        if (accentColor) {
          e.currentTarget.style.backgroundColor = accentColor
          e.currentTarget.style.color = accentInkColor
        }
      }}
      onMouseLeave={(e) => {
        if (accentColor) {
          e.currentTarget.style.backgroundColor = 'transparent'
          e.currentTarget.style.color = accentColor
        }
      }}
      icon={<LogoutIcon width="var(--menu-icon-size)" height="var(--menu-icon-size)" />}
    />
  )
}
